import { useState, useRef, useCallback, type JSX } from 'react';
import { SkipToRecomModal } from '../../pages/fit-survey/components/skip-to-recom-modal';
import { ExitModal } from '../../pages/scanner/exit-modal/exit-modal';
import { ExitModalsContext, type ExitModalsCtx } from './exit-modal-context';

interface SkipOpts {
  onSkip: () => void;
  onCloseExperience: () => void;
}

interface ExitOpts {
  onCloseSideBar: () => void;
}

export function ExitModalsProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  // Skip-to-recommendation modal
  const [skipOpen, setSkipOpen] = useState(false);
  const skipHandlers = useRef<SkipOpts | null>(null);

  const openSkipToRecom = useCallback((opts: SkipOpts) => {
    skipHandlers.current = opts;
    setSkipOpen(true);
  }, []);
  const closeSkipToRecom = useCallback(() => {
    setSkipOpen(false);
  }, []);

  const handleSkipCloseExperience = useCallback(() => {
    const h = skipHandlers.current;
    closeSkipToRecom();
    h?.onCloseExperience();
  }, [closeSkipToRecom]);

  const handleSkipConfirm = useCallback(() => {
    const h = skipHandlers.current;
    closeSkipToRecom();
    h?.onSkip();
  }, [closeSkipToRecom]);

  // Exit modal
  const [exitOpen, setExitOpen] = useState(false);
  const exitHandlers = useRef<ExitOpts | null>(null);

  const openExitSurvey = useCallback((opts: ExitOpts) => {
    exitHandlers.current = opts;
    setExitOpen(true);
  }, []);
  const closeExitSurvey = useCallback(() => {
    setExitOpen(false);
  }, []);

  const handleExitCloseSideBar = useCallback(() => {
    const h = exitHandlers.current;
    closeExitSurvey();
    h?.onCloseSideBar();
  }, [closeExitSurvey]);

  const handleExitCloseModal = useCallback(() => {
    closeExitSurvey();
  }, [closeExitSurvey]);

  const value: ExitModalsCtx = {
    openSkipToRecom,
    closeSkipToRecom,
    openExitSurvey,
    closeExitSurvey,
  };

  return (
    <ExitModalsContext.Provider value={value}>
      {children}

      <SkipToRecomModal
        show={skipOpen}
        onCloseExperience={handleSkipCloseExperience}
        onCloseModal={closeSkipToRecom}
        onSkip={handleSkipConfirm}
      />

      <ExitModal
        show={exitOpen}
        closeSideBar={handleExitCloseSideBar}
        closeModal={handleExitCloseModal}
      />
    </ExitModalsContext.Provider>
  );
}
